import { defineNuxtRouteMiddleware } from '#app'
import { PUBLIC_ROUTES, AUTH_ROUTES } from '~/constants/routes'

type TempSession = {
  firebaseUid: string
  redirectTo: string
  timestamp: number
  mfa_enabled: boolean
  mfa_verified: boolean
  userId: string
  email: string
}

async function revokeSessionAndRedirect(redirectPath: string) {
  if (!import.meta.client) {
    return navigateTo(redirectPath, { replace: true })
  }

  const xsrfToken = useCookie("XSRF-TOKEN", {
    maxAge: undefined,
    expires: undefined
  }).value

  if (xsrfToken) {
    try {
      const config = useRuntimeConfig()
      const apiUrl = config.public.API_URL
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-XSRF-TOKEN': xsrfToken
      }
      
      await $fetch("/api/logout", {
        baseURL: apiUrl,
        method: 'POST',
        credentials: 'include',
        headers
      }).catch(() => {})
    } catch (error) {
      console.error('Session revocation failed:', error)
    }
  }

  const { reset } = useAuth()
  reset()
  
  const xsrfCookie = useCookie("XSRF-TOKEN", {
    maxAge: undefined,
    expires: undefined
  })
  xsrfCookie.value = null

  return navigateTo(redirectPath, { replace: true })
}

export default defineNuxtRouteMiddleware(async (to) => {
  const route = to.path
  
  // Block access to register page - registration is disabled
  if (route === '/register') {
    return revokeSessionAndRedirect('/login')
  }
  
  // Redirect root path to login (landing page is hidden)
  if (route === '/') {
    if (import.meta.client) {
      const { isAuthenticated } = useAuth()
      const { isFirebaseAuthenticated } = useAuthFirebase()
      
      const tempSession = useSessionStorage<TempSession | null>('auth.temp', null)
      const session = tempSession.value as TempSession | null
      if (session && session.mfa_enabled && !session.mfa_verified) {
        return revokeSessionAndRedirect('/login')
      }
      
      const userIsAuthenticated = isAuthenticated.value || isFirebaseAuthenticated.value
      if (userIsAuthenticated) {
        return navigateTo('/dashboard', { replace: true })
      }
    }
    return revokeSessionAndRedirect('/login')
  }
  
  const config = useRuntimeConfig()
  const isPublicRoute = PUBLIC_ROUTES.some(publicRoute => publicRoute === '/' ? route === '/' : route === publicRoute || route.startsWith(publicRoute + '/'))

  if (isPublicRoute) {
    if (import.meta.client) {
      const { isAuthenticated } = useAuth()
      const { isFirebaseAuthenticated } = useAuthFirebase()
      
      const tempSession = useSessionStorage<TempSession | null>('auth.temp', null)
      const session = tempSession.value as TempSession | null
      if (session && session.mfa_enabled && !session.mfa_verified) {
        return
      }
      
      const userIsAuthenticated = isAuthenticated.value || isFirebaseAuthenticated.value
      if (userIsAuthenticated && (AUTH_ROUTES as readonly string[]).includes(route)) return navigateTo('/dashboard')
    }
    return
  }

  if (import.meta.server) {
    const cookieString = useRequestHeaders(['cookie']).cookie
    if (cookieString) return
  }
  
  const { isAuthenticated: isTraditionalAuth, authUser: traditionalAuthUser } = useAuth()
  const { isFirebaseAuthenticated, authUser: firebaseAuthUser } = useAuthFirebase()
  
  // Check for pending 2FA - if exists, redirect to login
  if (import.meta.client) {
    const tempSession = useSessionStorage<TempSession | null>('auth.temp', null)
    const session = tempSession.value as TempSession | null
    if (session && session.mfa_enabled && !session.mfa_verified) {
      return revokeSessionAndRedirect(`/login?redirectTo=${encodeURIComponent(to.fullPath)}`)
    }
  }
  
  const xsrfToken = useCookie("XSRF-TOKEN", {
    maxAge: undefined,
    expires: undefined
  }).value
  
  if (xsrfToken) {
    const { fetchCurrentUser } = useAuth()
    try {
      const user = await fetchCurrentUser()
      if (!user || !user.id) {
        return revokeSessionAndRedirect(`/login?redirectTo=${encodeURIComponent(to.fullPath)}`)
      }
      return
    } catch (error) {
      return revokeSessionAndRedirect(`/login?redirectTo=${encodeURIComponent(to.fullPath)}`)
    }
  }
  
  const hasAuthUser = !!traditionalAuthUser.value || !!firebaseAuthUser.value
  if (!hasAuthUser) {
    return revokeSessionAndRedirect(`/login?redirectTo=${encodeURIComponent(to.fullPath)}`)
  }
  
  if (isTraditionalAuth.value || isFirebaseAuthenticated.value) {
    const { fetchCurrentUser } = useAuth()
    try {
      const user = await fetchCurrentUser()
      if (!user || !user.id) {
        return revokeSessionAndRedirect(`/login?redirectTo=${encodeURIComponent(to.fullPath)}`)
      }
      return
    } catch (error) {
      return revokeSessionAndRedirect(`/login?redirectTo=${encodeURIComponent(to.fullPath)}`)
    }
  }
  
  return revokeSessionAndRedirect(`/login?redirectTo=${encodeURIComponent(to.fullPath)}`)
}) 